import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../common/SEO';
import Layout from '../common/Layout';
import PageHero, { HeroCtas } from '../components/ui/PageHero';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import { SectionHead } from '../components/ui/SectionHead';
import Notice from '../components/ui/Notice';
import StageList from '../components/ui/StageList';
import Button from '../components/ui/Button';
import Faq from '../components/ui/Faq';
import StatusChip from '../components/ui/StatusChip';
import {
  GateList,
  RoleMatrix,
  CapabilityList,
  LensList,
} from '../components/ui/PillarBlocks';
import { breadcrumbSchema } from '../components/ui/schema';

const emphasize = (text, terms) => {
  if (!terms || !terms.length) return text;
  const escaped = terms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`(${escaped.join('|')})`, 'g');
  return text
    .split(re)
    .map((part, i) =>
      terms.includes(part) ? (
        <strong key={i}>{part}</strong>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    );
};

const Approach = () => {
  const { t, i18n } = useTranslation('cy');
  const meta = t('approach.meta', { returnObjects: true });
  const stages = t('approach.hero.stages', { returnObjects: true });
  const gateRows = t('approach.gates.rows', { returnObjects: true });
  const pillars = t('approach.byPillar.columns', { returnObjects: true });

  const riskItems = t('approach.risk.rows', { returnObjects: true }).map((r) => ({
    title: r.risk,
    fields: [
      { label: t('ui.cols.control'), value: r.control },
      { label: t('ui.cols.escalate'), value: r.escalate, tone: 'judgement' },
    ],
  }));

  const schema = [
    breadcrumbSchema([{ label: t('nav.home'), to: '/' }, { label: t('nav.howWeInvest'), to: '/approach' }], i18n.language),
  ];

  return (
    <Layout>
      <SEO title={meta.title} description={meta.description} path="/approach" schema={schema} />

      <PageHero
        id="approach-hero"
        eyebrow={t('approach.hero.eyebrow')}
        title={t('approach.hero.h1')}
        kicker={t('approach.hero.kicker')}
        breadcrumb={<Breadcrumbs items={[{ label: t('nav.howWeInvest') }]} />}
      >
        <nav aria-label={t('ui.onThisPage')}>
          <ol className="cy-stage-flow cy-page-hero__stages">
            {stages.map((name, i) => (
              <li className="cy-stage-flow__item" key={name}>
                <a className="cy-stage-flow__link" href={`#approach-stage-${i}`}>
                  <span className="cy-stage__num">{String(i + 1).padStart(2, '0')}</span>
                  <span className="cy-stage-flow__name">{name}</span>
                </a>
              </li>
            ))}
          </ol>
        </nav>
        <Notice variant="inline" className="cy-hero__boundary">
          {t('approach.hero.boundary')}
        </Notice>
        <HeroCtas
          primary={t('approach.hero.cta')}
          primaryTo="/submit-opportunity"
          secondary={t('approach.hero.discuss')}
          secondaryTo="/contact?topic=partnership#contact-form"
        />
      </PageHero>

      <section className="cy-section cy-section--alt cy-zone--doc" id="approach-gates">
        <div className="cy-container">
          <SectionHead title={t('approach.gates.h2')} lead={t('approach.gates.lead')} />
          <p className="cy-gate-intro">{emphasize(t('approach.gates.intro'), t('approach.gates.emphasize', { returnObjects: true }))}</p>
          <p className="cy-fineprint cy-section-note">{t('approach.gates.lens')}</p>
          <GateList
            rows={gateRows}
            passLabel={t('ui.cols.pass')}
            stopLabel={t('ui.cols.stop')}
            featuredLabel={t('approach.gates.core')}
          />
          <p className="cy-section-action cy-section-action--row">
            {t('approach.gates.links', { returnObjects: true }).map((l) => (
              <Link className="cy-link" key={l.to} to={l.to}>{l.label}</Link>
            ))}
          </p>
        </div>
      </section>

      <section className="cy-section cy-zone--doc" id="approach-stages">
        <div className="cy-container">
          <SectionHead title={t('approach.stages.h2')} lead={t('approach.stages.lead')} />
          <StageList items={t('approach.stages.items', { returnObjects: true })} idPrefix="approach-stage" />
          <Notice variant="inline" className="cy-section-note">
            {t('approach.stages.risk')}{' '}
            <a className="cy-link" href="#approach-risk">{t('approach.stages.riskLink')}</a>
          </Notice>
        </div>
      </section>

      <section className="cy-section cy-section--alt cy-zone--doc" id="approach-roles">
        <div className="cy-container">
          <SectionHead title={t('approach.roles.h2')} lead={t('approach.roles.lead')} />
          <RoleMatrix
            rows={t('approach.roles.rows', { returnObjects: true })}
            titleKey="party"
            fields={[
              { key: 'responsible', label: t('ui.cols.responsible') },
              { key: 'decisions', label: t('ui.cols.decisions') },
            ]}
            notKey="notResponsible"
            notLabel={t('ui.cols.notResponsible')}
          />
          <p className="cy-fineprint cy-section-note">{t('approach.roles.note')}</p>
        </div>
      </section>

      <section className="cy-section cy-zone--doc" id="approach-capabilities">
        <div className="cy-container">
          <SectionHead title={t('approach.capabilities.h2')} lead={t('approach.capabilities.lead')} />
          <CapabilityList
            rows={t('approach.capabilities.rows', { returnObjects: true })}
            behaviourLabel={t('ui.cols.behaviour')}
            evidenceLabel={t('ui.cols.allowedEvidence')}
            forbiddenLabel={t('ui.cols.forbidden')}
          />
          <div className="cy-cap-status">
            <StatusChip variant="pending">{t('approach.capabilities.pendingChip')}</StatusChip>
            <span className="cy-fineprint">{t('approach.capabilities.note')}</span>
          </div>
          <p className="cy-section-action cy-section-action--row">
            <Link className="cy-link" to="/about#about-team">{t('approach.capabilities.cta')}</Link>
            <span aria-hidden="true">·</span>
            <a className="cy-link" href="#approach-gates">{t('approach.capabilities.gateLink')}</a>
          </p>
        </div>
      </section>

      <section className="cy-section cy-section--alt cy-zone--doc" id="approach-risk">
        <div className="cy-container">
          <SectionHead eyebrow={t('approach.risk.eyebrow')} title={t('approach.risk.h2')} lead={t('approach.risk.lead')} />
          <LensList items={riskItems} />
          <Notice variant="compliance" className="cy-section-note">
            {t('approach.risk.principle')}{' '}
            <Link className="cy-link" to="/terms#terms-investment-boundary">{t('approach.risk.link')}</Link>
          </Notice>
        </div>
      </section>

      <section className="cy-section cy-zone--doc" id="approach-faq">
        <div className="cy-container cy-container--reading">
          <SectionHead title={t('approach.faq.h2')} lead={t('approach.faq.lead')} />
          <Faq items={t('approach.faq.items', { returnObjects: true })} idPrefix="approach-faq" />
        </div>
      </section>

      <section className="cy-section cy-section--alt cy-zone--doc" id="approach-by-pillar">
        <div className="cy-container">
          <SectionHead title={t('approach.byPillar.h2')} lead={t('approach.byPillar.lead')} />
          <div className="cy-compare">
            {pillars.map((col) => (
              <article className="cy-compare__col" key={col.pillar}>
                <h3 className="cy-compare__title">{col.pillar}</h3>
                <dl className="cy-compare__dl">
                  <div>
                    <dt>{t('ui.byPillar.input')}</dt>
                    <dd>{col.input}</dd>
                  </div>
                  <div>
                    <dt>{t('ui.byPillar.risk')}</dt>
                    <dd>{col.risk}</dd>
                  </div>
                  <div>
                    <dt>{t('ui.byPillar.value')}</dt>
                    <dd>{col.value}</dd>
                  </div>
                  <div>
                    <dt>{t('ui.byPillar.material')}</dt>
                    <dd>{col.material}</dd>
                  </div>
                </dl>
                <p className="cy-section-action">
                  <Link className="cy-link" to={col.to}>{col.cta}</Link>
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="cy-section cy-section--cta" id="approach-final-cta">
        <div className="cy-container cy-cta-band">
          <h2 className="cy-title cy-cta-band__title">{t('approach.finalCta.h2')}</h2>
          <p className="cy-lede cy-cta-band__lede">{t('approach.finalCta.body')}</p>
          <div className="cy-cta-band__actions">
            <Button to="/submit-opportunity">{t('cta.submit')}</Button>
            <Button to="/contact?topic=partnership#contact-form" variant="secondary">{t('approach.finalCta.discuss')}</Button>
          </div>
          <p className="cy-fineprint cy-cta-band__privacy">
            {t('approach.finalCta.privacy')}{' '}
            <Link className="cy-link" to="/privacy">{t('ui.privacyPolicy')}</Link>
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default Approach;
